import { useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import Quill from 'quill';
import ReactQuill from 'react-quill-new';
import 'quill/dist/quill.snow.css';
import {
  normalizeQuillEmpty,
  prepareRichHtmlForEditor,
  htmlHasRichContent
} from '../utils/richHtml.js';
import { quillListsToSemanticHtml } from '../utils/quillSemanticLists.js';

function readEditorHtml(editor, fallbackHtml = '') {
  if (editor && typeof editor.getSemanticHTML === 'function') {
    try {
      return editor.getSemanticHTML();
    } catch {
      /* fall through */
    }
  }
  const rootHtml = editor?.root?.innerHTML;
  if (rootHtml != null && String(rootHtml).trim()) {
    return quillListsToSemanticHtml(String(rootHtml));
  }
  return quillListsToSemanticHtml(String(fallbackHtml || ''));
}

function editorVisibleText(editor) {
  return String(editor?.getText?.() || '')
    .replace(/\u00a0/g, ' ')
    .trim();
}

/**
 * Load external HTML into Quill 2. clipboard.convert often strips legacy markup;
 * dangerouslyPasteHTML + DOM fallback keeps admin editors in sync with the public site.
 */
function setEditorHtml(editor, html) {
  if (!editor) return;

  const prepared = prepareRichHtmlForEditor(html);
  if (!normalizeQuillEmpty(prepared)) {
    try {
      editor.setContents?.({ ops: [{ insert: '\n' }] }, 'silent');
    } catch {
      /* ignore */
    }
    return;
  }

  const forceDom = () => {
    if (editor.root) editor.root.innerHTML = prepared;
  };

  const pasteHtml = () => {
    if (!editor.clipboard?.dangerouslyPasteHTML) {
      forceDom();
      return;
    }
    try {
      const len = Math.max(0, (editor.getLength?.() ?? 1) - 1);
      if (len > 0) editor.deleteText(0, len, 'silent');
      editor.clipboard.dangerouslyPasteHTML(0, prepared, 'silent');
    } catch {
      forceDom();
    }
  };

  pasteHtml();

  if (htmlHasRichContent(prepared) && !editorVisibleText(editor)) {
    try {
      const delta = editor.clipboard?.convert?.({ html: prepared, text: '' });
      if (delta) editor.setContents(delta, 'silent');
    } catch {
      /* ignore */
    }
  }

  if (htmlHasRichContent(prepared) && !editorVisibleText(editor)) {
    forceDom();
  }
}

// Removed quill-table-better registration

/**
 * @param {{ current: ((file: File) => Promise<string|undefined>) | undefined }} uploadImageRef
 */
function createModules(uploadImageRef) {
  const handlers = {
    image() {
      const quill = this.quill;
      const uploadImage = uploadImageRef?.current;
      const insertUrl = (url) => {
        const trimmed = String(url || '').trim();
        if (!trimmed) return;
        const range = quill.getSelection(true) || { index: Math.max(0, quill.getLength() - 1) };
        quill.insertEmbed(range.index, 'image', trimmed, 'user');
        quill.setSelection(range.index + 1);
      };

      if (typeof uploadImage === 'function') {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.onchange = async () => {
          const file = input.files?.[0];
          if (!file) return;
          try {
            const url = await uploadImage(file);
            insertUrl(url);
          } catch {
            const fallback =
              typeof window !== 'undefined' ? window.prompt('Paste image URL', '') : '';
            insertUrl(fallback);
          }
        };
        input.click();
        return;
      }
      const url = typeof window !== 'undefined' ? window.prompt('Image URL', '') : '';
      insertUrl(url);
    }
  };

  return {
    table: true,
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        [{ color: [] }, { background: [] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['blockquote', 'code-block'],
        ['link', 'image'],
        [{ 'table-action': ['', 'insert-row-above', 'insert-row-below', 'insert-column-left', 'insert-column-right', 'delete-row', 'delete-column', 'delete-table'] }],
        ['clean']
      ],
      handlers: {
        'table-action': function (value) {
          if (!value) return;
          const table = this.quill.getModule('table');
          if (!table) return;
          if (value === 'insert-row-above') table.insertRowAbove();
          if (value === 'insert-row-below') table.insertRowBelow();
          if (value === 'insert-column-left') table.insertColumnLeft();
          if (value === 'insert-column-right') table.insertColumnRight();
          if (value === 'delete-row') table.deleteRow();
          if (value === 'delete-column') table.deleteColumn();
          if (value === 'delete-table') table.deleteTable();
        },
        ...handlers
      }
    }
  };
}

/**
 * Quill 2 + react-quill: never pass API HTML as defaultValue/controlled value —
 * react-quill runs clipboard.convert on init and often clears legacy HTML.
 * Imperatively sync when props change from outside (API load).
 */
const AdminRichTextEditor = forwardRef(function AdminRichTextEditor(
  { value, onChange, placeholder, hint, className = '', compact = false, uploadImage },
  ref
) {
  const wrapperRef = useRef(null);
  const quillRef = useRef(null);
  const uploadImageRef = useRef(uploadImage);
  uploadImageRef.current = uploadImage;
  const mountTimeRef = useRef(typeof performance !== 'undefined' ? performance.now() : 0);
  const lastEmittedHtmlRef = useRef('');
  const lastSyncedPropRef = useRef('');
  const skipExternalSyncRef = useRef(false);
  const modulesMemo = useMemo(() => createModules(uploadImageRef), []);

  useImperativeHandle(
    ref,
    () => ({
      getHtml() {
        const api = quillRef.current;
        const editor = api && typeof api.getEditor === 'function' ? api.getEditor() : null;
        return readEditorHtml(editor);
      },
      applyHtml(html) {
        const api = quillRef.current;
        const editor = api && typeof api.getEditor === 'function' ? api.getEditor() : null;
        if (!editor?.root) return;
        const next = prepareRichHtmlForEditor(html);
        setEditorHtml(editor, next || '<p><br></p>');
        lastEmittedHtmlRef.current = next;
        lastSyncedPropRef.current = next;
        skipExternalSyncRef.current = true;
      }
    }),
    []
  );

  /** Push API / parent HTML into Quill when it changes externally — not after local typing/paste. */
  useEffect(() => {
    const next = prepareRichHtmlForEditor(value);

    if (skipExternalSyncRef.current) {
      skipExternalSyncRef.current = false;
      lastSyncedPropRef.current = next;
      return;
    }

    const want = normalizeQuillEmpty(next);
    const prev = normalizeQuillEmpty(lastSyncedPropRef.current);
    if (want === prev) return;

    lastSyncedPropRef.current = next;

    const syncFromValue = () => {
      const api = quillRef.current;
      const editor = api && typeof api.getEditor === 'function' ? api.getEditor() : null;
      const root = editor?.root;
      if (!editor || !root) return;

      const cur = normalizeQuillEmpty(root.innerHTML);
      if (cur === want) return;

      setEditorHtml(editor, next);
      if (htmlHasRichContent(next)) {
        lastEmittedHtmlRef.current = next;
        skipExternalSyncRef.current = true;
      }
    };

    syncFromValue();
    const delays = [0, 50, 120, 300, 600];
    const timers = delays.map((ms) => window.setTimeout(syncFromValue, ms));
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [value]);

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return;
    const enforceToolbarButtonTypes = () => {
      root.querySelectorAll('.ql-toolbar button').forEach((btn) => {
        btn.setAttribute('type', 'button');
      });
    };
    enforceToolbarButtonTypes();
    const observer = new MutationObserver(() => {
      enforceToolbarButtonTypes();
    });
    observer.observe(root, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
    };
  }, []);

  const handleChange = (_nextHtml, _delta, _source, editor) => {
    const quillEditor =
      quillRef.current && typeof quillRef.current.getEditor === 'function'
        ? quillRef.current.getEditor()
        : null;
    const rawHtml =
      typeof editor?.getHTML === 'function'
        ? editor.getHTML()
        : quillEditor?.root?.innerHTML || '';
    let s = normalizeQuillEmpty(quillListsToSemanticHtml(rawHtml));

    const visibleText = String(quillEditor?.getText?.() || editor?.getText?.() || '')
      .replace(/\u00a0/g, ' ')
      .trim();
    if (!s && visibleText) {
      s = rawHtml.trim() || `<p>${visibleText}</p>`;
    }

    const sinceMount =
      typeof performance !== 'undefined' ? performance.now() - mountTimeRef.current : 999;
    const hadContent = Boolean(normalizeQuillEmpty(lastEmittedHtmlRef.current));
    if (sinceMount < 400 && !s && hadContent) {
      return;
    }

    if (s === normalizeQuillEmpty(lastEmittedHtmlRef.current)) {
      return;
    }

    lastEmittedHtmlRef.current = s;
    skipExternalSyncRef.current = true;
    onChange?.(s);
  };

  return (
    <div
      ref={wrapperRef}
      className={`admin-rich-text-wrap schedule-rich-editor-wrap${compact ? ' admin-rich-text-wrap--compact' : ''} ${className}`.trim()}
    >
      {hint ? (
        <p
          className="trek-editor-hint"
          style={{ marginTop: '-0.25rem', marginBottom: '0.35rem' }}
        >
          {hint}
        </p>
      ) : null}
      <ReactQuill
        ref={quillRef}
        theme="snow"
        defaultValue=""
        onChange={handleChange}
        modules={modulesMemo}
        placeholder={placeholder || ''}
      />
    </div>
  );
});

AdminRichTextEditor.displayName = 'AdminRichTextEditor';

export default AdminRichTextEditor;
